import { create } from 'zustand'
import { runToFill } from '../hooks/shared/runToFill'

// Globální fronta dotahování (viz doc/plans/to-fill-data-fetching.md, krok 6).
// JEDEN worker, JEDNA fronta — sériové dotahování i napříč více hooky + jeden
// souhrnný progress přes celou app.
//
// Položky nesou `key` (JSON.stringify(queryKey) z hooku), aby se daly cíleně
// přerušit při přepnutí obrazovky (cancelKey).

const norm = (endpoint) =>
  endpoint?.includes('phenomena') ? 'stanic' : 'řad'

// Nahradí položku ve frontě novým statusem (immutably).
const mark = (queue, target, status) =>
  queue.map((q) => (q === target ? { ...q, status } : q))

export const useFillQueueStore = create((set, get) => ({
  active: false,
  done: 0,
  total: 0,
  queue: [], // [{ key, item, onFilled, status: 'pending'|'ok'|'skip'|'aborted' }]
  // Timestamp posledního doběhnutí fronty, kde se aspoň jedna položka dotáhla.
  // Grafy (server-side z DB) na jeho změnu přenačtou chartUrl (cache-bust). 0 = nikdy.
  lastFilledAt: 0,
  _running: false,
  _curKey: null,
  _abort: null,
  _filledInRun: 0,

  // key: string (z queryKey), jobs: [{ toFill: [...], onFilled }]
  enqueue: ({ key, jobs }) => {
    const flat = jobs.flatMap((j) =>
      (j.toFill ?? []).map((item) => ({
        key,
        item,
        onFilled: j.onFilled,
        onSettled: j.onSettled,
        status: 'pending',
      }))
    )
    if (!flat.length) return
    // Dedup — nepřidávej položku se stejným key+endpoint+params, která už čeká.
    const sig = (e) => `${e.key}|${e.item.endpoint}|${JSON.stringify(e.item.params)}`
    const existing = new Set(get().queue.filter((q) => q.status === 'pending').map(sig))
    const fresh = flat.filter((e) => !existing.has(sig(e)))
    if (!fresh.length) return
    set((s) => ({
      queue: [...s.queue, ...fresh],
      total: s.total + fresh.length,
      active: true,
    }))
    get()._tick()
  },

  // Je položka (key + endpoint + params) už zařazená ve frontě (čeká na zpracování)?
  // Slouží useToFill k dedup — nenabízet znovu to, co už čeká.
  has: (key, item) => {
    const target = `${key}|${item.endpoint}|${JSON.stringify(item.params)}`
    return get().queue.some(
      (q) => q.status === 'pending'
        && `${q.key}|${q.item.endpoint}|${JSON.stringify(q.item.params)}` === target
    )
  },

  // Přeruší nezpracované položky daného klíče + běžící, pokud je toho klíče.
  cancelKey: (key) => {
    if (get()._curKey === key) get()._abort?.abort()
    set((s) => ({
      queue: s.queue.filter((q) => q.key !== key || q.status !== 'pending'),
    }))
    get()._maybeFinish()
  },

  cancelAll: () => {
    get()._abort?.abort()
    set((s) => ({
      queue: [],
      active: false,
      done: 0,
      total: 0,
      _running: false,
      _curKey: null,
      _abort: null,
      _filledInRun: 0,
      lastFilledAt: s._filledInRun > 0 ? Date.now() : s.lastFilledAt,
    }))
  },

  // Text do progress panelu: "Načítání 3/10 řad"
  label: () => {
    const s = get()
    const next = s.queue.find((q) => q.status === 'pending') ?? s.queue[s.queue.length - 1]
    return `${s.done}/${s.total} ${norm(next?.item?.endpoint)}`
  },

  _tick: async () => {
    if (get()._running) return
    const next = get().queue.find((q) => q.status === 'pending')
    if (!next) {
      get()._maybeFinish()
      return
    }
    const ctrl = new AbortController()
    set({ _running: true, _curKey: next.key, _abort: ctrl })

    let filled = false
    await runToFill([next.item], {
      signal: ctrl.signal,
      onFilled: (resp, item) => {
        if (ctrl.signal.aborted) return
        filled = true
        next.onFilled?.(resp, item)
      },
      onSettled: (item, resp, itemFilled) => {
        if (ctrl.signal.aborted) return
        next.onSettled?.(item, resp, itemFilled)
      },
    })

    set((s) => ({
      _running: false,
      _curKey: null,
      _abort: null,
      done: s.done + 1,
      _filledInRun: s._filledInRun + (filled ? 1 : 0),
      queue: mark(s.queue, next, ctrl.signal.aborted ? 'aborted' : 'ok'),
    }))
    get()._tick()
  },

  _maybeFinish: () => {
    const s = get()
    if (!s._running && !s.queue.some((q) => q.status === 'pending')) {
      set({
        active: false,
        done: 0,
        total: 0,
        queue: [],
        _curKey: null,
        _abort: null,
        _filledInRun: 0,
        lastFilledAt: s._filledInRun > 0 ? Date.now() : s.lastFilledAt,
      })
    }
  },
}))
